/**
 * Document theme model.
 *
 * A theme is a small config object (a preset id + a sparse set of overrides)
 * that resolves to a flat CSS-variable map applied to the document surface
 * (the TipTap `.docs-editor` and the read/print view). See `compute.ts` for the
 * config → CSS-var mapping, and `presets.ts` for the built-in presets.
 *
 * Design intent (tdd-0045):
 *  - Font sizes are DERIVED, not per-tag. `baseFontSize` scales everything;
 *    `fontScale` is the modular-scale ratio that sets the step between heading
 *    levels. One knob moves the whole hierarchy coherently.
 *  - Spacing is a single multiplier over a base unit.
 *  - No per-tag (h1 vs h2) customisation surface — deliberately excluded.
 */

/** Curated font-family options. Values are real CSS `font-family` stacks. */
export type FontFamilyId =
  | "sans"
  | "serif"
  | "mono"
  | "system"
  | "geist"
  | "inter"
  | "source-serif"
  | "ibm-plex-mono"
  // OS-installed stacks — no web-font loading required.
  | "georgia"
  | "times"
  | "garamond"
  | "trebuchet"
  | "verdana"
  | "tahoma";

/** Heading levels that can independently take the heading colour. */
export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

export const HEADING_LEVELS: HeadingLevel[] = [1, 2, 3, 4, 5, 6];

/**
 * Non-heading elements that may also take the heading colour.
 *
 * Deliberately a short list. Every entry here is a place where a second colour
 * reads as intentional emphasis; adding `p` or `li` would just be a slower way
 * of setting `bodyColor`, and colouring links would fight the accent.
 */
export type ColourableElement = "strong" | "blockquote" | "code";

export const COLOURABLE_ELEMENTS: ColourableElement[] = [
  "strong",
  "blockquote",
  "code",
];

export interface ThemeOverrides {
  /** Accent colour → `--doc-accent` (links, blockquote rule, etc.). CSS color. */
  accent?: string;
  /** Body text colour → `--doc-foreground`. CSS color. */
  bodyColor?: string;
  /**
   * Document surface colour → `--doc-background`. CSS color.
   *
   * Paired with `bodyColor`: a preset that pins a dark ink tone is assuming a
   * light page under it, so it must state that page colour rather than inherit
   * the app chrome's (which follows dark mode and would leave dark-on-dark).
   */
  background?: string;
  /**
   * Heading colour. Unset (the default for every preset) means headings inherit
   * `bodyColor`, so adding this knob changed nothing about how existing
   * documents render.
   */
  headingColor?: string;
  /**
   * Which heading levels take `headingColor`. Defaults to all six. Levels not
   * listed keep `bodyColor`, so a theme can colour just H1/H2 and leave the
   * deeper levels as body ink.
   */
  headingColorLevels?: HeadingLevel[];
  /**
   * Extra elements that also take `headingColor`. Empty by default. These are
   * the few where a second colour reads as deliberate rather than noisy —
   * see `COLOURABLE_ELEMENTS`.
   */
  headingColorElements?: ColourableElement[];

  /** Body font family. */
  fontBody?: FontFamilyId;
  /** Heading font family (falls back to body when unset). */
  fontHeading?: FontFamilyId;
  /** Monospace font family (code / pre). */
  fontMono?: FontFamilyId;

  /** Body font weight (100–900). */
  bodyWeight?: number;
  /** Heading font weight (100–900). */
  headingWeight?: number;
  /** Render headings in italic. */
  italicHeadings?: boolean;

  /** Master font size in px — scales the entire type hierarchy. */
  baseFontSize?: number;
  /** Modular-scale ratio between heading levels (e.g. 1.2 = minor third). */
  fontScale?: number;
  /** Spacing multiplier over the base spacing unit. */
  spacing?: number;
}

export interface DocTheme {
  /** Preset id (see PRESETS). Unknown ids resolve to the default preset. */
  preset: string;
  /** Sparse overrides layered on top of the preset's baseline. */
  overrides?: ThemeOverrides;
}

/**
 * The knobs a preset may leave unset. `headingColor` is genuinely optional —
 * "unset" is the meaningful default (headings inherit `bodyColor`), so forcing
 * all 11 presets to restate that would be noise rather than specification.
 */
type OptionalPresetKnob =
  | "headingColor"
  | "headingColorLevels"
  | "headingColorElements";

/**
 * A preset is a fully-specified baseline — every knob has a value, so the
 * computed CSS-var map is complete even with no user overrides. The exception
 * is `OptionalPresetKnob`, where absence is itself the default.
 */
export interface ThemePreset {
  id: string;
  name: string;
  description: string;
  base: Required<Omit<ThemeOverrides, OptionalPresetKnob>> &
    Pick<ThemeOverrides, OptionalPresetKnob>;
  /**
   * Optional explicit size table (rem), used VERBATIM only while the user has
   * not touched the `baseFontSize` / `fontScale` knobs. This lets a preset pin
   * an irregular hand-tuned scale (the "default" preset reproduces today's
   * exact sizes) while still switching to the derived modular scale the moment
   * either sizing knob is overridden. Keyed h1…h6 + body.
   */
  fixedSizesRem?: {
    h1: number;
    h2: number;
    h3: number;
    h4: number;
    h5: number;
    h6: number;
    body: number;
  };
}
